import React, { useState } from 'react';
import axios from 'axios';
import { utmPayload, trackCta } from './tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const INDUSTRIES = [
  ['real_estate', 'Real Estate / Realtor'], ['mortgage', 'Mortgage Broker'], ['financial_advisor', 'Financial Advisor / Planner'],
  ['investment', 'Investment Services'], ['insurance', 'Insurance'], ['construction', 'Construction / Contractor'],
  ['home_builder', 'Home Builder / Developer'], ['cleaning', 'Professional Cleaning'], ['property_management', 'Property Management'],
  ['airbnb', 'Airbnb / Short-Term Rental'], ['hospitality', 'Hospitality'], ['restaurant', 'Restaurant / Catering'],
  ['event_planner', 'Event Planner'], ['wedding_planner', 'Wedding Planner'], ['photographer', 'Photographer / Videographer'],
  ['home_staging', 'Home Staging'], ['interior_design', 'Interior Design'], ['legal', 'Legal Services'],
  ['accounting', 'Accounting / Bookkeeping'], ['automotive', 'Automotive'], ['retail', 'Retail'], ['health_wellness', 'Health & Wellness'],
  ['beauty', 'Beauty / Personal Care'], ['technology', 'Technology'], ['consulting', 'Consulting'], ['non_profit', 'Non-Profit / Association'],
  ['corporate', 'Corporate / Enterprise'], ['other', 'Other'],
];

const BOTTLE_RANGES = [
  ['unsure', 'Not sure yet'], ['under_30', 'Under 30 bottles (1 kit)'], ['30_60', '30 – 60 bottles (1–2 kits)'],
  ['60_120', '60 – 120 bottles (2–4 kits)'], ['120_240', '120 – 240 bottles (4–8 kits)'], ['240_plus', '240+ bottles'],
];

const INITIAL = {
  firstName: '', lastName: '', businessName: '', email: '', phone: '', industry: 'real_estate',
  bottleRange: 'unsure', useCase: '', dateNeeded: '', customLabel: 'later', designNotes: '', message: '', marketingConsent: true,
};
const ACCEPT = 'application/pdf,image/png,image/jpeg,image/svg+xml';
const ALLOWED_TYPES = new Set(ACCEPT.split(','));
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 };

const errorMessage = (err) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return `${detail.message}${detail.reference ? ` Reference: ${detail.reference}` : ''}`;
  if (err.message) return err.message;
  return 'Something went wrong — your request is not marked complete. Please try again.';
};

const CorpInquiryForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submissionId] = useState(() => window.crypto?.randomUUID?.() || `corp-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const selectFiles = (event) => {
    setError('');
    const selected = Array.from(event.target.files || []);
    if (selected.length > 5) {
      event.target.value = '';
      setFiles([]);
      setError('Upload up to 5 artwork files.');
      return;
    }
    const invalidType = selected.find((file) => !ALLOWED_TYPES.has(file.type));
    if (invalidType) {
      event.target.value = '';
      setFiles([]);
      setError(`${invalidType.name} is not a supported PDF, PNG, JPG, or SVG file.`);
      return;
    }
    const tooLarge = selected.find((file) => file.size > MAX_FILE_BYTES);
    if (tooLarge) {
      event.target.value = '';
      setFiles([]);
      setError(`${tooLarge.name} is larger than 25 MB.`);
      return;
    }
    setFiles(selected);
  };

  const next = () => {
    setError('');
    if (!form.firstName.trim()) return setError('Please share your first name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError('Enter a valid email.');
    if (form.phone.replace(/\D/g, '').length < 7) return setError('Enter a valid phone number.');
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    trackCta('partner', 'inquiry_submit', false);
    try {
      const payload = {
        ...form,
        firstName: form.firstName.trim(), lastName: form.lastName.trim() || null,
        businessName: form.businessName.trim() || null, email: form.email.trim().toLowerCase(), phone: form.phone.trim(),
        useCase: form.useCase.trim() || null, dateNeeded: form.dateNeeded.trim() || null, message: form.message.trim() || null,
        designNotes: form.designNotes.trim() || null,
        customLabel: files.length ? 'yes' : form.customLabel,
        pendingUploads: files.length,
        submissionId,
        website: honeypot,
        ...utmPayload(),
      };
      const { data } = await axios.post(`${API}/corp/inquiry`, payload);
      if (!data?.accepted || !data?.inquiryId) throw new Error('Submission was not accepted');
      let uploaded = 0;
      let retained = 0;
      let completed = data;
      if (files.length && data.inquiryId) {
        const fd = new FormData();
        files.forEach((f) => fd.append('files', f));
        fd.append('upload_token', data.uploadToken || '');
        const up = await axios.post(`${API}/corp/inquiry/${data.inquiryId}/labels`, fd);
        uploaded = up.data?.uploaded || 0;
        retained = up.data?.retained || 0;
        completed = up.data;
      }
      if (!completed?.emailConfirmed || completed?.emailStatus !== 'sent') {
        throw new Error('Both required emails were not confirmed.');
      }
      setResult({ ...data, ...completed, uploaded, retained, attempted: files.length });
    } catch (err) {
      setError(errorMessage(err));
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <div className="ff-confirmation-badge ff-text-center" data-testid="corp-inquiry-thankyou" style={{ padding: 32 }}>
        <h3 className="ff-serif" style={{ margin: '10px 0 8px', color: 'var(--ff-burgundy)', fontSize: 26 }}>We've Got It! 🍷</h3>
        <p style={{ margin: 0, color: 'var(--ff-brown)', fontSize: 15, lineHeight: 1.6 }} data-testid="corp-inquiry-success-message">
          Your request and both confirmation emails were accepted by our email provider. We'll review your information and connect with you
          shortly about bringing your custom wine to life.
        </p>
        <p style={{ margin: '10px 0 0', color: 'var(--ff-brown-soft)', fontSize: 14 }}>
          Check your inbox for confirmation.
          {result.retained > 0 && ` We securely retained ${result.retained} label file${result.retained > 1 ? 's' : ''}.`}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="ff-card" style={{ padding: 24, position: 'relative' }} data-testid="corp-inquiry-form">
      <div className="ff-form-progress" style={{ marginBottom: 18 }}>
        <span className={step === 1 ? 'on' : 'done'}>1 · Contact</span>
        <span className={step === 2 ? 'on' : ''}>2 · Your wine & label</span>
      </div>

      <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off"
             style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} aria-hidden="true" data-testid="corp-honeypot" />

      {step === 1 && (
        <>
          <div style={grid}>
            <div className="ff-field"><label htmlFor="corp-first">First name *</label>
              <input id="corp-first" data-testid="corp-name" value={form.firstName} onChange={(e) => update('firstName', e.target.value)} required /></div>
            <div className="ff-field"><label htmlFor="corp-last">Last name <span className="ff-optional">optional</span></label>
              <input id="corp-last" data-testid="corp-lastname" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} /></div>
          </div>
          <div className="ff-field"><label htmlFor="corp-biz">Business / organization <span className="ff-optional">optional</span></label>
            <input id="corp-biz" data-testid="corp-biz" value={form.businessName} onChange={(e) => update('businessName', e.target.value)} /></div>
          <div style={grid}>
            <div className="ff-field"><label htmlFor="corp-email">Email *</label>
              <input id="corp-email" data-testid="corp-email" type="email" autoComplete="email" value={form.email} onChange={(e) => update('email', e.target.value)} required /></div>
            <div className="ff-field"><label htmlFor="corp-phone">Phone *</label>
              <input id="corp-phone" data-testid="corp-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} required /></div>
          </div>
          {error && <div className="ff-error" role="alert" style={{ marginBottom: 12 }} data-testid="corp-inquiry-error">{error}</div>}
          <button type="button" className="ff-btn ff-btn-primary ff-btn-block" onClick={next} data-testid="corp-inquiry-next">Continue →</button>
        </>
      )}

      {step === 2 && (
        <>
          <div style={grid}>
            <div className="ff-field"><label htmlFor="corp-industry">Industry</label>
              <select id="corp-industry" data-testid="corp-industry" value={form.industry} onChange={(e) => update('industry', e.target.value)}>
                {INDUSTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div className="ff-field"><label htmlFor="corp-range">Estimated bottles / kits</label>
              <select id="corp-range" data-testid="corp-range" value={form.bottleRange} onChange={(e) => update('bottleRange', e.target.value)}>
                {BOTTLE_RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
          </div>
          <div style={grid}>
            <div className="ff-field"><label htmlFor="corp-usecase">Intended use <span className="ff-optional">optional</span></label>
              <input id="corp-usecase" data-testid="corp-usecase" placeholder="e.g. Christmas client gifts, closing gifts, welcome bottles" value={form.useCase} onChange={(e) => update('useCase', e.target.value)} /></div>
            <div className="ff-field"><label htmlFor="corp-date">Desired completion date <span className="ff-optional">optional</span></label>
              <input id="corp-date" data-testid="corp-date" placeholder="e.g. before Dec 15, 2026" value={form.dateNeeded} onChange={(e) => update('dateNeeded', e.target.value)} /></div>
          </div>

          <div className="ff-field">
            <label>Custom label? <span style={{ fontWeight: 400, color: 'var(--ff-brown-soft)' }}>($1.50 per label)</span></label>
            <div className="ff-radio-group" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {[['yes', 'Yes'], ['later', "I'll decide later"], ['no', 'No label needed']].map(([v, l]) => (
                <label key={v} className={`ff-radio-tile ${form.customLabel === v ? 'selected' : ''}`} data-testid={`corp-label-${v}`}>
                  <input type="radio" name="customLabel" value={v} checked={form.customLabel === v} onChange={() => update('customLabel', v)} /> {l}
                </label>
              ))}
            </div>
          </div>

          {form.customLabel !== 'no' && (
            <div className="ff-field" data-testid="corp-label-upload-field">
              <label htmlFor="corp-files">Upload your label design <span className="ff-optional">optional</span></label>
              <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--ff-brown-soft)' }}>
                Already have your artwork? Upload it here (PDF, PNG, JPG or SVG, up to 5 files, 25 MB each). Not ready yet? No problem — you can decide later.
              </p>
              <input id="corp-files" type="file" multiple accept={ACCEPT} data-testid="corp-files"
                     onChange={selectFiles} />
              {files.length > 0 && <p style={{ margin: '6px 0 0', fontSize: 13 }} data-testid="corp-files-list">{files.map((f) => f.name).join(', ')}</p>}
              <label className="ff-checkbox-row" style={{ marginTop: 10 }}>
                <input type="checkbox" data-testid="corp-label-provide-later-checkbox" checked={form.customLabel === 'later'} onChange={(e) => update('customLabel', e.target.checked ? 'later' : 'yes')} />
                <span style={{ fontSize: 13, color: 'var(--ff-brown-soft)' }}>I'll provide my label design later</span>
              </label>
            </div>
          )}

          {form.customLabel !== 'no' && (
            <div className="ff-field"><label htmlFor="corp-design-notes">Label design notes <span className="ff-optional">optional</span></label>
              <textarea id="corp-design-notes" data-testid="corp-design-notes" rows={3} value={form.designNotes} onChange={(e) => update('designNotes', e.target.value)} /></div>
          )}

          <div className="ff-field"><label htmlFor="corp-msg">Additional information <span className="ff-optional">optional</span></label>
            <textarea id="corp-msg" data-testid="corp-msg" rows={3} value={form.message} onChange={(e) => update('message', e.target.value)} /></div>

          <label className="ff-checkbox-row">
            <input type="checkbox" data-testid="corp-consent" checked={form.marketingConsent} onChange={(e) => update('marketingConsent', e.target.checked)} />
            <span style={{ fontSize: 13, color: 'var(--ff-brown-soft)' }}>Send me occasional updates from The Lokal. You can opt out any time.</span>
          </label>

          {error && <div className="ff-error" role="alert" style={{ marginBottom: 12 }} data-testid="corp-inquiry-error">{error}</div>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="ff-btn ff-btn-ghost" onClick={() => setStep(1)} disabled={submitting} data-testid="corp-inquiry-back">← Back</button>
            <button type="submit" className="ff-btn ff-btn-primary" style={{ flex: 1 }} disabled={submitting} data-testid="corp-inquiry-submit">
              {submitting ? 'Sending…' : 'Create Your Corporate Wine →'}
            </button>
          </div>
          <p style={{ marginTop: 12, fontSize: 12, color: 'var(--ff-brown-soft)', textAlign: 'center' }}>
            Approved corporate partners save 15% on their wine order.
          </p>
        </>
      )}
    </form>
  );
};

export default CorpInquiryForm;
